/**
 * Reading GraphQL errors off an arriere client rejection.
 *
 * Two things are nested inside a thrown `GraphQLClientError`: the useful
 * message and the machine-readable code. `response.errors[0].message` is the
 * subgraph's own sentence (the client's `errorsParser` has already stripped the
 * router's `SUBREQUEST_HTTP_ERROR` wrapper, so index 0 is the real one), and
 * `extensions.code` carries the rm-api `AppError` code.
 *
 * `extensions` is deliberately typed away by the generated client
 * (`ResponseData.errors` is `{ message: string }[]`) but is present at runtime,
 * which is why this reads through a structural cast. That is the whole reason
 * this helper exists: matching on the message text instead does not work — see
 * `RevenueAgentsStore.rollbackDecision`, whose `DECISION_NOT_ROLLBACKABLE`
 * branches are dead code because that string only ever appears in `extensions`.
 */

interface GraphqlErrorShape {
  message?: string
  extensions?: { code?: string; statusCode?: number }
}

export interface ExtractedGraphqlError {
  /** `extensions.code` from the first error, e.g. `AUTO_EXECUTION_NOT_CANCELLABLE`. */
  code: string | null
  /** `extensions.statusCode` — the transport status is always 200, so this is
   *  the only place an HTTP code like 409 appears. */
  statusCode: number | null
  /** The server's own sentence, safe to show verbatim. */
  message: string
}

export function extractGraphqlError(err: unknown): ExtractedGraphqlError {
  if (err && typeof err === 'object') {
    const maybe = err as {
      response?: { errors?: GraphqlErrorShape[] }
      message?: string
    }
    const first = maybe.response?.errors?.[0]
    if (first?.message) {
      return {
        code: first.extensions?.code ?? null,
        statusCode: first.extensions?.statusCode ?? null,
        message: first.message,
      }
    }
    if (maybe.message) return { code: null, statusCode: null, message: maybe.message }
  }
  return { code: null, statusCode: null, message: 'Unknown error' }
}

/** Message-only convenience for callers that do not branch on the code. */
export function extractGraphqlErrorMessage(err: unknown): string {
  return extractGraphqlError(err).message
}
